/**
 * CommandPalette — Global ⌘K search & navigation
 *
 * Navigation stays DSL-driven.
 * Object search, bookmarks, recents, work, and signals come from work-surface SDK.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, ArrowRight, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useLayoutConfigContext } from '@/lib/use-layout-config';
import { resolveIcon } from '@/lib/icon-map';
import { workSurfaceSdk, type OmWorkspaceSearchItem } from '@/lib/work-surface-sdk';

interface SearchItem {
  slug: string;
  label: string;
  labelPlural: string;
  icon: string;
  category: string;
  categoryLabel?: string;
  path: string;
}

interface TenantOption {
  id: string;
  name: string;
  slug?: string;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string, title: string, icon?: string) => void;
  tenants: TenantOption[];
  currentTenantId?: string;
  currentUserId?: string;
  onSwitchTenant: (tenantId: string, groupId?: string) => void | Promise<void>;
}

type PaletteRow =
  | { key: string; section: 'Navigation'; type: 'navigation'; title: string; subtitle?: string; icon: string; path: string }
  | { key: string; section: 'Workspace'; type: 'workspace'; title: string; subtitle?: string; icon: string; item: OmWorkspaceSearchItem }
  | { key: string; section: 'Tenants'; type: 'tenant'; title: string; subtitle?: string; icon: 'building-2'; tenantId: string; groupId?: string };

function isExternalPath(path?: string | null): boolean {
  return Boolean(path && /^https?:\/\//i.test(path));
}

export function CommandPalette({
  open,
  onClose,
  onNavigate,
  tenants,
  currentTenantId,
  currentUserId,
  onSwitchTenant,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [workspaceResults, setWorkspaceResults] = useState<OmWorkspaceSearchItem[]>([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { config } = useLayoutConfigContext();
  const isCcAdminShell = typeof window !== 'undefined' && window.location.port === '3002';
  const searchPlaceholder = isCcAdminShell ? '搜索菜单、页面、数据' : t('search.placeholder', '搜索菜单、页面、数据');

  const resolveItemLabel = (item: { label: string; labelPlural: string; labelKey?: string; labelPluralKey?: string }, mode: 'single' | 'plural' = 'plural') => {
    if (mode === 'plural') {
      if (item.labelPluralKey) return t(item.labelPluralKey, item.labelPlural);
      if (item.labelKey) return t(item.labelKey, item.labelPlural);
      return t(`nav.${item.labelPlural}`, item.labelPlural);
    }
    if (item.labelKey) return t(item.labelKey, item.label);
    return t(`nav.${item.label}`, item.label);
  };

  const navigationItems: SearchItem[] = useMemo(() => [
    { slug: 'dashboard', label: t('dashboard.title'), labelPlural: t('dashboard.title'), icon: 'dashboard', category: 'nav', path: '/' },
    ...config.navigation.flatMap((cat) =>
      cat.items.map((item) => ({
        slug: item.slug,
        label: resolveItemLabel(item, 'single'),
        labelPlural: resolveItemLabel(item, 'plural'),
        icon: item.icon ?? 'briefcase',
        category: cat.id,
        categoryLabel: cat.labelKey ? t(cat.labelKey, cat.label) : t(`category.${cat.id}`, cat.label),
        path: item.route,
      })),
    ),
  ], [config.navigation, t]);

  const filteredNavigation = useMemo(() => {
    if (!query) return navigationItems.slice(0, 12);
    const normalized = query.toLowerCase();
    return navigationItems.filter((item) =>
      item.label.toLowerCase().includes(normalized)
      || item.labelPlural.toLowerCase().includes(normalized)
      || item.slug.toLowerCase().includes(normalized)
      || item.category.toLowerCase().includes(normalized),
    );
  }, [navigationItems, query]);

  const filteredTenants = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const rows = tenants.filter((tenant) => tenant.id !== currentTenantId);
    if (!normalized) return rows.slice(0, 5);
    return rows.filter((tenant) => `${tenant.name} ${tenant.slug ?? ''}`.toLowerCase().includes(normalized)).slice(0, 5);
  }, [currentTenantId, query, tenants]);

  const rows = useMemo<PaletteRow[]>(() => [
    ...filteredNavigation.map((item) => ({
      key: `nav:${item.slug}`,
      section: 'Navigation' as const,
      type: 'navigation' as const,
      title: item.labelPlural,
      subtitle: item.categoryLabel ?? t(`category.${item.category}`),
      icon: item.icon,
      path: item.path,
    })),
    ...workspaceResults.map((item) => ({
      key: `workspace:${item.kind}:${item.id}`,
      section: 'Workspace' as const,
      type: 'workspace' as const,
      title: item.title,
      subtitle: item.subtitle,
      icon: item.icon ?? (item.kind === 'signal'
        ? 'triangle-alert'
        : item.kind === 'work_item'
          ? 'list-todo'
          : item.kind === 'entity'
            ? 'briefcase'
            : item.kind === 'saved_view'
              ? 'bookmark-check'
              : 'bookmark'),
      item,
    })),
    ...filteredTenants.map((tenant) => ({
      key: `tenant:${tenant.id}`,
      section: 'Tenants' as const,
      type: 'tenant' as const,
      title: tenant.name,
      subtitle: tenant.slug ?? 'Switch tenant',
      icon: 'building-2' as const,
      tenantId: tenant.id,
    })),
  ], [filteredNavigation, filteredTenants, t, workspaceResults]);

  useEffect(() => setSelectedIndex(0), [query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelectedIndex(0);
    setWorkspaceResults([]);
    setWorkspaceError(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      setWorkspaceLoading(true);
      setWorkspaceError(null);
      try {
        const result = await workSurfaceSdk.searchWorkspace(query, 8);
        setWorkspaceResults(result.items);
      } catch (error) {
        setWorkspaceResults([]);
        setWorkspaceError(error instanceof Error ? error.message : 'Failed to search workspace');
      } finally {
        setWorkspaceLoading(false);
      }
    }, 220);
    return () => clearTimeout(timer);
  }, [open, query]);

  const openWorkspaceItem = useCallback(async (item: OmWorkspaceSearchItem) => {
    const ownerUserId = typeof item.metadata?.ownerUserId === 'string' ? item.metadata.ownerUserId : null;
    const isOwnBookmark = !ownerUserId || ownerUserId === currentUserId;
    if (item.kind === 'bookmark') {
      if (isOwnBookmark) {
        const result = await workSurfaceSdk.openBookmark(item.id);
        const target = workSurfaceSdk.resolveRoute({
          route: result.bookmark.route,
          subjectRef: result.bookmark.subjectRef,
        });
        if (!target) return;
        if (result.bookmark.openMode === 'new_tab' || isExternalPath(target)) {
          window.open(target, '_blank', 'noopener,noreferrer');
        } else {
          onNavigate(target, result.bookmark.title, result.bookmark.icon);
        }
        onClose();
        return;
      }

      const target = workSurfaceSdk.resolveRoute({
        route: item.route,
        subjectRef: item.subjectRef,
      });
      if (!target) return;
      await workSurfaceSdk.trackRecent({
        title: item.title,
        moduleRef: item.moduleRef,
        route: target,
        subjectRef: item.subjectRef,
        icon: item.icon,
        metadata: {
          searchKind: item.kind,
          sourceBookmarkId: item.id,
          openedFrom: 'management_search',
          ...item.metadata,
        },
      }).catch(() => undefined);
      if (item.openMode === 'new_tab' || isExternalPath(target)) {
        window.open(target, '_blank', 'noopener,noreferrer');
      } else {
        onNavigate(target, item.title, item.icon);
      }
      onClose();
      return;
    }

    const target = workSurfaceSdk.resolveRoute({
      route: item.route,
      subjectRef: item.subjectRef,
    });
    if (!target) return;

    try {
      await workSurfaceSdk.trackRecent({
        title: item.title,
        moduleRef: item.moduleRef,
        route: target,
        subjectRef: item.subjectRef,
        icon: item.icon,
        metadata: {
          searchKind: item.kind,
          ...item.metadata,
        },
      });
    } catch {
      // Search-open should still navigate even if recent tracking fails.
    }

    if (item.openMode === 'new_tab' || isExternalPath(target)) {
      window.open(target, '_blank', 'noopener,noreferrer');
    } else {
      onNavigate(target, item.title, item.icon);
    }
    onClose();
  }, [currentUserId, onClose, onNavigate]);

  const handleSelect = useCallback(async (row: PaletteRow | undefined) => {
    if (!row) return;
    if (row.type === 'navigation') {
      onNavigate(row.path, row.title, row.icon);
      onClose();
      return;
    }
    if (row.type === 'tenant') {
      await onSwitchTenant(row.tenantId, row.groupId);
      onClose();
      return;
    }
    await openWorkspaceItem(row.item);
  }, [onClose, onNavigate, onSwitchTenant, openWorkspaceItem]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;
      const totalCount = rows.length;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((index) => Math.min(index + 1, Math.max(totalCount - 1, 0)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((index) => Math.max(index - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        void handleSelect(rows[selectedIndex]);
      } else if (e.key === 'Escape') {
        onClose();
      }
    },
    [handleSelect, onClose, open, rows, selectedIndex],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!open) return null;

  let lastSection: PaletteRow['section'] | null = null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-[15vh] backdrop-blur-sm"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="w-full max-w-xl overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-2xl">
        <div className="flex items-center gap-3 border-b px-4 py-3">
          <Search className="h-5 w-5 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
          <kbd className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">esc</kbd>
        </div>

        <div className="max-h-[420px] overflow-y-auto py-2">
          {rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {workspaceLoading ? 'Searching workspace…' : workspaceError ?? t('search.noResults')}
            </div>
          ) : (
            <>
              {rows.map((row, index) => {
                const isSelected = index === selectedIndex;
                const Icon = row.icon === 'building-2' ? Building2 : resolveIcon(row.icon);
                const section = row.section !== lastSection ? row.section : null;
                lastSection = row.section;

                return (
                  <div key={row.key}>
                    {section ? (
                      <div className="px-4 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {section}
                      </div>
                    ) : null}
                    <button
                      onClick={() => void handleSelect(row)}
                      className={cn(
                        'flex w-full items-center gap-3 px-4 py-2 transition-colors',
                        isSelected ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
                      )}
                    >
                      <div className={cn('rounded-lg p-2', isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1 text-left">
                        <p className={cn('truncate text-sm font-medium', isSelected && 'text-primary')}>{row.title}</p>
                        {row.subtitle ? <p className="truncate text-xs text-muted-foreground">{row.subtitle}</p> : null}
                      </div>
                      {isSelected ? <ArrowRight className="h-4 w-4 text-primary" /> : null}
                    </button>
                  </div>
                );
              })}
              {workspaceLoading ? <div className="px-4 py-2 text-xs italic text-muted-foreground">Searching workspace…</div> : null}
            </>
          )}
        </div>

        <div className="flex items-center justify-between border-t bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="rounded bg-muted px-1">↑</kbd>
              <kbd className="rounded bg-muted px-1">↓</kbd>
              {t('search.navigate')}
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded bg-muted px-1">⏎</kbd>
              {t('search.open')}
            </span>
          </div>
          <span>{t('search.results', { count: rows.length })}</span>
        </div>
      </div>
    </div>
  );
}
